import { canRetryChunkRecovery } from './chunkError';
import { isAppServiceWorkerRegistration, shouldUseServiceWorker } from './serviceWorker';

export type ChunkUpdateRecoveryState = 'idle' | 'updating' | 'failed';

const RELOAD_ATTEMPT_KEY = 'cgt-hub-chunk-recovery-attempts';
const MAX_RELOAD_ATTEMPTS = 2;
const OPERATION_TIMEOUT_MS = 2500;
const APP_CACHE_PREFIXES = ['cgt-hub-app-v', 'cgt-hub-html-cache-v'];
const listeners = new Set<() => void>();
let state: ChunkUpdateRecoveryState = 'idle';
let recoveryInProgress: Promise<void> | null = null;

const notify = () => listeners.forEach(listener => listener());

export const subscribeToChunkUpdateRecovery = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const getChunkUpdateRecoveryState = () => state;

const setState = (nextState: ChunkUpdateRecoveryState) => {
  state = nextState;
  notify();
};

const withTimeout = <T>(operation: Promise<T>, fallback: T): Promise<T> =>
  new Promise(resolve => {
    const timeout = window.setTimeout(() => resolve(fallback), OPERATION_TIMEOUT_MS);
    operation.then(
      value => {
        window.clearTimeout(timeout);
        resolve(value);
      },
      () => {
        window.clearTimeout(timeout);
        resolve(fallback);
      },
    );
  });

const updateAppWorker = async () => {
  if (!('serviceWorker' in navigator)) return;
  const registrations = await withTimeout(
    navigator.serviceWorker.getRegistrations(),
    [] as ServiceWorkerRegistration[],
  );
  const appRegistrations = registrations.filter(registration =>
    isAppServiceWorkerRegistration(registration, window.location.origin),
  );

  await Promise.all(
    appRegistrations.map(async registration => {
      await withTimeout(registration.update(), undefined);
      const waitingWorker = registration.waiting;
      if (!waitingWorker) return;

      const controllerChanged = new Promise<void>(resolve => {
        const onControllerChange = () => {
          navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
          resolve();
        };
        navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);
        window.setTimeout(() => {
          navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
          resolve();
        }, OPERATION_TIMEOUT_MS);
      });
      waitingWorker.postMessage({ type: 'SKIP_WAITING' });
      await controllerChanged;
    }),
  );
};

const clearAppCaches = async () => {
  if (!('caches' in window)) return;
  const names = await withTimeout(caches.keys(), [] as string[]);
  await Promise.all(
    names
      .filter(name => APP_CACHE_PREFIXES.some(prefix => name.startsWith(prefix)))
      .map(name => withTimeout(caches.delete(name), false)),
  );
};

const reloadWithFreshDocument = () => {
  const url = new URL(window.location.href);
  url.searchParams.set('__app_update', String(Date.now()));
  let attempts = 1;
  try {
    attempts = Number(sessionStorage.getItem(RELOAD_ATTEMPT_KEY) ?? '1');
  } catch {
    // Keep the query parameter as the cross-reload fallback.
  }
  url.searchParams.set('__app_update_attempt', String(attempts));
  window.location.replace(url.toString());
};

export const recoverFromStaleChunk = (): Promise<void> => {
  if (!shouldUseServiceWorker()) return Promise.resolve();
  if (recoveryInProgress) return recoveryInProgress;

  let attempts = 0;
  try {
    attempts = Number(
      sessionStorage.getItem(RELOAD_ATTEMPT_KEY) ??
        new URLSearchParams(window.location.search).get('__app_update_attempt') ??
        '0',
    );
  } catch {
    attempts = Number(
      new URLSearchParams(window.location.search).get('__app_update_attempt') ?? '0',
    );
  }

  if (!canRetryChunkRecovery(attempts, MAX_RELOAD_ATTEMPTS)) {
    setState('failed');
    return Promise.resolve();
  }

  try {
    sessionStorage.setItem(RELOAD_ATTEMPT_KEY, String(attempts + 1));
  } catch {
    // The reload URL carries the attempt count when storage is unavailable.
  }

  setState('updating');
  recoveryInProgress = (async () => {
    await withTimeout(updateAppWorker(), undefined);
    await withTimeout(clearAppCaches(), undefined);
    reloadWithFreshDocument();
  })()
    .catch(error => {
      console.error('Could not recover from a stale application chunk:', error);
      setState('failed');
    })
    .finally(() => {
      recoveryInProgress = null;
    });

  return recoveryInProgress;
};

export const retryStaleChunkRecovery = () => {
  try {
    sessionStorage.removeItem(RELOAD_ATTEMPT_KEY);
  } catch {
    // A manual retry is still allowed when session storage is unavailable.
  }
  const url = new URL(window.location.href);
  url.searchParams.delete('__app_update_attempt');
  window.history.replaceState(window.history.state, '', url.toString());
  recoveryInProgress = null;
  return recoverFromStaleChunk();
};

export const clearSuccessfulChunkRecovery = () => {
  if (!new URLSearchParams(window.location.search).has('__app_update')) return;
  window.setTimeout(() => {
    try {
      sessionStorage.removeItem(RELOAD_ATTEMPT_KEY);
    } catch {
      // Ignore unavailable session storage.
    }
    const url = new URL(window.location.href);
    url.searchParams.delete('__app_update');
    url.searchParams.delete('__app_update_attempt');
    window.history.replaceState(window.history.state, '', url.toString());
  }, 30_000);
};
